import CustomerContactSelect from "components/Customer/CustomerContactSelect";
import { BlobProvider } from "@react-pdf/renderer";
import Spinner from "components/Spinner";
import SelectEmailTemplate from "components/WorkOrderOrderEmail/SelectEmailTemplate";
import WOFileManager from "components/WorkOrderOrderEmail/WOFileManager";
import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import ReactQuill from "react-quill";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import { truncateString } from "utils/UtilityFuncs";
import { WOPdf } from "components/Pdfs/WOPdf/WOPdf";
import SearchWoForApproval from "./SearchWoForApproval";

// Shared width for the "Email To" / "Subject" label columns. Sized to the
// longer of the two labels so their inputs line up, while keeping the gap far
// tighter than the old col-2 (a twelfth of the modal).
const LABEL_COL_WIDTH = 75;

export default function SendWoApproval({
  woId,
  selectedWO,
  woProducts,
  approvalData,
  refreshFunc,
  btnClass,
  btnColor,
  btnTitle,
  useQuestionTemplate = false,
  // TRUE when the customer has approved every card. Resending then is never
  // what anyone means: the server keeps the approved cards but rolls the
  // ORDER back (adminReviewed -> pending, awaitingCustomerResponse -> true),
  // which parks a finished order in Pending waiting for an answer the
  // customer already gave -- the W-9660 shape. Staff who genuinely need the
  // customer to look again reset the card in question first, which un-approves
  // it and re-enables this button.
  //
  // Read from allApproved (or its WorkOrders mirror, approvalStatus) rather
  // than the cards, so every page that renders this button agrees with the
  // list badges, which read the same flag.
  customerApproved = false,
}) {
  const dispatch = useDispatch();

  /*   All States
   ********************************************* */
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showFilesModal, setShowFilesModal] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState(null);

  const [showCustomerContact, setShowCustomerContact] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState(null);

  const [locations, setLocations] = useState(null);
  const [userSmtp, setUserSmtp] = useState(null);

  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [woProductsList, setWoProductsList] = useState(null);
  const [woProductsWithSignedImages, setWoProductsWithSignedImages] = useState(
    [],
  );
  const [approvalColorMap, setApprovalColorMap] = useState(null);
  const [locationLogo, setLocationLogo] = useState(null);
  const [loadingPdfAssets, setLoadingPdfAssets] = useState(false);
  // Approved date for the PDF header, from the products fetch.
  const [approvedAt, setApprovedAt] = useState(null);

  /*   Email States
   **************** */
  const [emailTo, setEmailTo] = useState(null);
  const [subject, setSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [templateSettings, setTemplateSettings] = useState({
    sendPdf: false,
    ccSalesPerson: false,
    showStatuses: false,
  });

  const modules = {
    toolbar: [
      [{ font: [] }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ indent: "-1" }, { indent: "+1" }, { align: [] }],
      ["clean"],
    ],
  };

  /*   All Functions
   ********************************************* */
  // Function to uncheck and check other checkboxes
  // based on "ALL" checkbox

  function loadingOn() {
    setIsLoading(true);
  }

  function loadingOff() {
    setIsLoading(false);
  }

  const checkUserLocationSmtp = useCallback(async () => {
    if (!selectedWorkOrder?.locationId) {
      setUserSmtp(null);
      return;
    }

    try {
      loadingOn();
      const { data } = await API.post(
        "/user/smtp/check",
        {
          locationId: selectedWorkOrder?.locationId,
        },
        {
          headers: authHeader(),
        },
      );

      if (data?.success) {
        setUserSmtp({ smtpEmail: data?.smtpEmail });
      } else {
        throw new Error(data?.message || "Failed to get smtp details");
      }

      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  }, [dispatch, selectedWorkOrder?.locationId]);

  const normalizeEmailHtml = (html) => {
    if (!html) return "";
    let cleaned = html.replace(/\u00A0/g, " ");
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(cleaned, "text/html");
      doc.querySelectorAll("p").forEach((p) => {
        const textOnly = (p.textContent || "").replace(/\s/g, "");
        const hasOnlyBr =
          p.querySelectorAll("br").length > 0 && textOnly === "";
        if (textOnly === "" && !hasOnlyBr) {
          p.remove();
        } else {
          p.style.margin = "0";
        }
      });
      doc.body.innerHTML = doc.body.innerHTML.replace(
        /(<br\s*\/?>\s*){2,}/gi,
        "<br />",
      );
      return doc.body.innerHTML;
    } catch (err) {
      return cleaned;
    }
  };

  const getFirstName = (obj) => obj?.firstName || "";
  const getLastName = (obj) => obj?.lastName || "";

  const escapeRegExp = (value) =>
    String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const replaceVariableToken = (text, label, replacement) => {
    const normalizedLabelPattern = escapeRegExp(label).replace(/\s+/g, "\\s+");
    const pattern = new RegExp(
      `(?:&lt;&lt;|<<)\\s*${normalizedLabelPattern}\\s*(?:&gt;&gt;|>>)`,
      "gi",
    );
    return String(text || "").replace(pattern, replacement ?? "");
  };

  const replaceTemplateVariables = (val) => {
    const woId = selectedWorkOrder?.woId || "";
    const customerId = selectedWorkOrder?.customer?.customerId || "";
    const customerName = selectedWorkOrder?.customerName || "";
    const jobName = selectedWorkOrder?.jobName || "";

    let firstName = "";
    let lastName = "";
    if (selectedWorkOrder?.contactData) {
      const contactObj =
        typeof selectedWorkOrder?.contactData === "string"
          ? JSON.parse(selectedWorkOrder?.contactData)
          : selectedWorkOrder?.contactData;
      firstName = getFirstName(contactObj);
      lastName = getLastName(contactObj);
    }

    const replacements = {
      ID: woId,
      "Customer ID": customerId,
      "Customer Name": customerName,
      "Job Name": jobName,
      "First Name": firstName,
      "Last Name": lastName,
      "Location Name": selectedWorkOrder?.location?.name || "",
    };

    return Object.entries(replacements).reduce(
      (updatedText, [label, replacement]) =>
        replaceVariableToken(updatedText, label, replacement),
      String(val || ""),
    );
  };

  const replaceSubjectVariables = (val) => {
    const woId = selectedWorkOrder?.woId || "";
    const customerId = selectedWorkOrder?.customer?.customerId || "";
    const customerName = selectedWorkOrder?.customerName || "";
    const jobName = selectedWorkOrder?.jobName || "";

    let firstName = "";
    let lastName = "";
    if (selectedWorkOrder?.contactData) {
      const contactObj =
        typeof selectedWorkOrder?.contactData === "string"
          ? JSON.parse(selectedWorkOrder?.contactData)
          : selectedWorkOrder?.contactData;
      firstName = getFirstName(contactObj);
      lastName = getLastName(contactObj);
    }

    const replacements = {
      ID: woId,
      "Customer ID": customerId,
      "Customer Name": customerName,
      "Job Name": jobName,
      "First Name": firstName,
      "Last Name": lastName,
      "Location Name": selectedWorkOrder?.location?.name || "",
    };

    return Object.entries(replacements).reduce(
      (updatedText, [label, replacement]) =>
        replaceVariableToken(updatedText, label, replacement),
      String(val || ""),
    );
  };

  const applyTemplate = (templateData) => {
    const body = templateData?.template ?? templateData?.format ?? "";
    const templateSubject = templateData?.subject || "";
    setEmailBody(replaceTemplateVariables(body));
    setSubject(replaceSubjectVariables(templateSubject));
    setTemplateSettings({
      sendPdf: Boolean(templateData?.sendPdf),
      ccSalesPerson: Boolean(templateData?.ccSalesPerson),
    });
  };

  const addEmailToList = (list, email) => {
    if (!email) return list || "";
    const normalizedEmail = String(email).trim();
    if (!normalizedEmail) return list || "";

    const parts = String(list || "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

    const exists = parts.some(
      (item) => item.toLowerCase() === normalizedEmail.toLowerCase(),
    );
    if (exists) return parts.join(", ");
    return [...parts, normalizedEmail].join(", ");
  };

  const preloadImageAsDataUrl = async (url) => {
    if (!url) return null;
    // Already a data URL or base64 — no need to fetch
    if (url.startsWith("data:")) return url;
    try {
      const response = await fetch(url, { mode: "cors" });
      if (!response.ok) return null;
      const blob = await response.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  };

  const getProductImagesWithSignedUrls = async (wOId) => {
    try {
      setLoadingPdfAssets(true);
      const { data } = await API.get(`/wo-products-with-images/${wOId}`, {
        headers: authHeader(),
      });

      if (data?.success) {
        setApprovedAt(data?.approvedAt || null);
        let colorDetails = data?.colorDetails;
        if (colorDetails) {
          let colorMap = {};
          colorDetails.forEach((color) => {
            colorMap[color.colorId] = color.name;
          });
          setApprovalColorMap(colorMap);
        } else {
          setApprovalColorMap(null);
        }

        // Pre-download all product images as base64 data URLs
        // so they are guaranteed available when the PDF renders
        const products = data?.data || [];
        const productsWithPreloadedImages = await Promise.all(
          products.map(async (product) => {
            if (product?.productImage) {
              const dataUrl = await preloadImageAsDataUrl(product.productImage);
              return {
                ...product,
                productImage: dataUrl || product.productImage,
              };
            }
            return product;
          }),
        );

        setWoProductsWithSignedImages(productsWithPreloadedImages);
      } else {
        setWoProductsWithSignedImages([]);
        setApprovalColorMap(null);
      }
    } catch (err) {
      setWoProductsWithSignedImages([]);
      setApprovalColorMap(null);
    } finally {
      setLoadingPdfAssets(false);
    }
  };

  const getLocationLogo = async () => {
    if (!selectedWorkOrder?.location?.pdfLogo) {
      setLocationLogo(null);
      return;
    }

    try {
      const { data } = await API.post(
        "/design-request/get-image",
        { path: selectedWorkOrder?.location?.pdfLogo },
        {
          headers: authHeader(),
        },
      );

      // Pre-download logo as base64 if it's a URL
      if (data && typeof data === "string" && !data.startsWith("data:")) {
        const dataUrl = await preloadImageAsDataUrl(data);
        setLocationLogo(dataUrl || data);
      } else {
        setLocationLogo(data);
      }
    } catch (err) {
      setLocationLogo(null);
    }
  };

  // get All Location
  const getAllLocations = async () => {
    try {
      const { data } = await API.get("/locations", {
        headers: authHeader(),
      });
      setLocations(data);
    } catch (err) {
      alert(err.message);
    }
  };

  const getFileName = (fileName) => {
    return fileName?.split("/")[fileName?.split("/").length - 1];
  };

  const handleDeleteFile = (item) => {
    let set = new Set(selectedFiles);
    set.delete(item);
    setSelectedFiles([...set]);
  };

  const handleClose = () => {
    reset();
    setShowApprovalModal(false);
  };

  const sendApproval = async (sendEmail = true, pdfBlob = null) => {
    if (!userSmtp?.smtpEmail) {
      return alert("Smtp Email is required!");
    }
    // alert("Function in development mode!");
    loadingOn();
    const formData = new FormData();

    /*   Files Operation
     **************** */
    formData.append("WoId", selectedWorkOrder?.woId);
    formData.append("wId", selectedWorkOrder?.id);
    formData.append("subject", subject);
    formData.append("emailBody", normalizeEmailHtml(emailBody));
    formData.append("emailTo", emailTo);
    formData.append("selectedFiles", JSON.stringify(selectedFiles));
    formData.append("sendEmail", sendEmail);
    // "Ask a Question" sends an email and NOTHING else (user, 2026-10-16).
    // The server writes the send stats -- last sent, sent by, awaiting
    // response, the approval stage, and it rolls the order back to pending --
    // for a real approval send. A question is not an approval request, so it
    // must not count as one. Same endpoint, this flag turns those writes off.
    formData.append("isQuestion", useQuestionTemplate ? "true" : "false");

    if (templateSettings?.sendPdf && pdfBlob) {
      formData.append("file", pdfBlob, `WO-${selectedWorkOrder?.woId}.pdf`);
      formData.append("sendPlayerNumberingSheet", true);
    }

    try {
      const { data } = await API.post(
        "/wo-lot-approvals/send-approval",
        formData,
        {
          headers: {
            ...authHeader(),
            "Content-Type": "multipart/form-data",
          },
        },
      );
      if (data?.success) {
        dispatch(
          setNotification({
            message: data.message,
            type: "success",
          }),
        );
        if (!sendEmail) {
          window.open(`${data.approvalLink}?viewOnly=true`, "_blank");
        } else {
          await refreshFunc();
          setShowApprovalModal(false);
        }
        loadingOff();
        // refreshFunc();
        return;
      } else {
        dispatch(
          setNotification({
            message: data.message || "Failed to send approval",
            type: "error",
          }),
        );
      }
      loadingOff();
    } catch (err) {
      dispatch(
        setNotification({
          message: err.response.data.message,
          type: "error",
        }),
      );
      // loadingOff();
    } finally {
      loadingOff();
    }
  };

  const getWorkOrderByWoId = async (woId) => {
    try {
      const { data } = await API.get(
        "/wo-lot-approvals/with-products/" + woId,
        {
          headers: authHeader(),
        },
      );
      if (data?.success) {
        setSelectedWorkOrder(data?.data || null);
        setWoProductsList(data?.data?.WOProducts || null);
      } else {
        dispatch(
          setNotification({
            message: data?.message || "Failed to fetch work order",
            type: "error",
          }),
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.response.data.message,
          type: "error",
        }),
      );
    }
  };

  const addSalesPersonEmailToList = () => {
    if (!templateSettings?.ccSalesPerson) return;
    const salesEmail = selectedWorkOrder?.salesPerson?.email;

    if (!salesEmail) {
      return dispatch(
        setNotification({
          message: "Sales person email not found",
          type: "error",
        }),
      );
    }

    if (!salesEmail) return;
    setEmailTo((prev) => {
      const next = addEmailToList(prev, salesEmail);
      return next === String(prev || "") ? prev : next;
    });
  };

  const reset = () => {
    if (!selectedWO) {
      setSelectedWorkOrder(null);
      setWoProductsList(null);
    }
    setEmailTo(null);
    setSubject("");
    setEmailBody("");
    setSelectedLocation(null);
    setSelectedFiles(null);
    setSelectedContacts(null);
    setTemplateSettings({
      sendPdf: false,
      ccSalesPerson: false,
      showStatuses: false,
    });
    setWoProductsWithSignedImages([]);
    setApprovalColorMap(null);
    setLocationLogo(null);
    setLoadingPdfAssets(false);
    setIsLoading(false);
  };

  const handleSelectContact = (contactsList) => {
    let contactList = emailTo;
    contactsList.forEach((element) => {
      contactList = contactList + ", " + element;
    });
    setEmailTo(contactList);
    setShowCustomerContact(false);
  };

  /* ******************** UseEffects ************************* */
  // Check if the work order has a valid shipping address
  const hasValidShippingAddress = (workOrder) => {
    if (!workOrder || !workOrder.shippingAddress) return false;

    try {
      // Parse the shipping address JSON (it could be a string or an object)
      const addressObj =
        typeof workOrder.shippingAddress === "string"
          ? JSON.parse(workOrder.shippingAddress)
          : workOrder.shippingAddress;

      // Parse the inner JSON if it's still a string
      const parsedAddress =
        typeof addressObj === "string" ? JSON.parse(addressObj) : addressObj;

      // Check if essential address fields exist
      return !!(
        parsedAddress &&
        (parsedAddress.addressLine1 || parsedAddress.address1) &&
        parsedAddress.city &&
        parsedAddress.state &&
        parsedAddress.zip
      );
    } catch (err) {
      console.error("Error parsing shipping address:", err);
      return false;
    }
  };

  useEffect(() => {
    if (selectedWO && woProducts && !woId && showApprovalModal) {
      setSelectedWorkOrder(selectedWO);
      setWoProductsList(woProducts);
    }
  }, [selectedWO, woProducts, woId, showApprovalModal]);

  useEffect(() => {
    if (showApprovalModal) {
      // setEmailTo(null);
      if (selectedWorkOrder?.contactData) {
        let email_to =
          typeof selectedWorkOrder?.contactData === "string"
            ? JSON.parse(selectedWorkOrder?.contactData)
            : selectedWorkOrder?.contactData;

        if (email_to) {
          let baseEmail = email_to.email || "";
          if (
            templateSettings?.ccSalesPerson &&
            selectedWorkOrder?.salesPerson?.email
          ) {
            baseEmail = addEmailToList(
              baseEmail,
              selectedWorkOrder.salesPerson.email,
            );
          }
          setEmailTo(baseEmail);
        }
        // set selected location
        if (locations && locations.length > 0) {
          const loc = locations.find(
            (loc) => loc.id === selectedWorkOrder.locationId,
          );
          setSelectedLocation(loc || null);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkOrder, locations, showApprovalModal]);

  useEffect(() => {
    if (!templateSettings?.ccSalesPerson) return;
    const salesEmail = selectedWorkOrder?.salesPerson?.email;
    if (!salesEmail) return;
    setEmailTo((prev) => {
      const next = addEmailToList(prev, salesEmail);
      return next === String(prev || "") ? prev : next;
    });
  }, [templateSettings?.ccSalesPerson, selectedWorkOrder?.salesPerson?.email]);

  useEffect(() => {
    let timeout = setTimeout(() => {
      if (selectedWorkOrder && showApprovalModal) {
        checkUserLocationSmtp();
        getAllLocations();
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [selectedWorkOrder, showApprovalModal, checkUserLocationSmtp]);

  useEffect(() => {
    if (selectedContacts) {
      let contactList = emailTo;
      selectedContacts.forEach((element) => {
        contactList = contactList + ", " + element;
      });
      setEmailTo(contactList);
    }
  }, [selectedContacts]);

  useEffect(() => {
    if (woId && !selectedWO && showApprovalModal) {
      getWorkOrderByWoId(woId);
    }
  }, [woId, selectedWO, woProducts, showApprovalModal]);

  useEffect(() => {
    const getLocationDefaultTemplate = async () => {
      const locationId =
        selectedWorkOrder?.locationId ||
        selectedWorkOrder?.location?.id ||
        selectedWorkOrder?.location?.locationId ||
        null;

      if (!locationId || !showApprovalModal) {
        return;
      }

      try {
        const { data } = await API.get(
          `/locations/${locationId}/templates/approval`,
          {
            headers: authHeader(),
          },
        );

        if ((data?.success || data?.type === "success") && data?.data) {
          if (useQuestionTemplate) {
            const body = data.data?.questionTemplate || "";
            const templateSubject = data.data?.questionSubject || "";
            setEmailBody(replaceTemplateVariables(body));
            setSubject(replaceSubjectVariables(templateSubject));
          } else {
            applyTemplate(data.data);
          }
        }
      } catch (err) {
        // ignore missing default template
      }
    };

    const timeout = setTimeout(() => {
      getLocationDefaultTemplate();
    }, 50);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkOrder, showApprovalModal]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (showApprovalModal && selectedWorkOrder?.id) {
        getProductImagesWithSignedUrls(selectedWorkOrder.id);
      }

      if (showApprovalModal && selectedWorkOrder?.location?.pdfLogo) {
        getLocationLogo();
      }
    }, 50);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showApprovalModal, selectedWorkOrder]);

  return (
    <div>
      {/* The email body editor fills whatever vertical space is left in the
          modal's scroll area instead of a fixed height -- ReactQuill renders
          a toolbar + container, so the container has to be the flex child
          that grows (and scrolls internally on long bodies). */}
      <style>{`
        .send-approval-quill {
          display: flex;
          flex-direction: column;
          flex: 1 1 auto;
          min-height: 0;
        }
        .send-approval-quill .ql-container {
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
        }
      `}</style>
      <button
        onClick={() => {
          setShowApprovalModal(true);
        }}
        type="button"
        className={`${btnClass} ${btnColor}`}
        disabled={customerApproved}
        title={
          customerApproved
            ? "The customer has approved everything. Reset a card first if it needs to go back to them."
            : undefined
        }
      >
        {btnTitle && btnTitle}
      </button>
      {/*   Product Modal
       ********************************************* */}
      <Modal
        size="lg"
        contentClassName="bg-transparent"
        show={showApprovalModal}
        onHide={() => handleClose()}
        style={{
          zIndex: 1000000001,
        }}
      >
        <Modal.Body className="bg-white custom-shadow custom-border-radius px-4 py-3">
          {!selectedWorkOrder ? (
            <>
              <SearchWoForApproval
                setSelectedWorkOrder={setSelectedWorkOrder}
                setWoProductsList={setWoProductsList}
              />
            </>
          ) : !selectedWorkOrder?.shipmentMethod ? (
            <div>
              <h3 className="text-danger">
                This work order does not have a shipping method.
              </h3>
              <p className="alert alert-warning text-black">
                A shipping method is required before sending an approval. Please
                add a shipping method to this work order first.
              </p>
            </div>
          ) : (
            <>
              {/* {!hasValidShippingAddress(selectedWorkOrder) ? (
                <div>
                  <h3 className="text-danger">
                    This work order does not have a valid shipping address.
                  </h3>
                  <p className="alert alert-warning text-black">
                    A shipping address is required before sending an approval.
                    Please add a shipping address to this work order first.
                  </p>
                  <div className="text-end my-3 mb-1">
                    <button
                      type="button"
                      className="btn btn-secondary me-1"
                      onClick={() => {
                        handleClose();
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              ) :  */}
              {!selectedWorkOrder?.location?.pdfLogo ? (
                <div>
                  <h3 className="text-danger">
                    This work order has no location logo.
                  </h3>
                  <div className="text-end my-3 mb-1">
                    <button
                      type="button"
                      className="btn btn-secondary me-1"
                      onClick={() => {
                        handleClose();
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              ) : woProducts && Object.keys(woProducts).length === 0 ? (
                <>
                  <div>
                    <h3 className="text-danger">
                      No Products This work order has no products.
                    </h3>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <h3>
                      {useQuestionTemplate
                        ? "Ask a Question For"
                        : "Send Approval For"}{" "}
                      (
                      <span className="mx-2 h3">
                        W-{selectedWorkOrder?.woId}{" "}
                        {truncateString(selectedWorkOrder?.jobName, 30)}
                      </span>
                      )
                    </h3>
                    <div
                      style={{
                        height: "70vh",
                        overflowY: "auto",
                        overflowX: "hidden",
                      }}
                      className="pe-1 d-flex flex-column"
                    >
                    <hr className="mb-2 mt-1" />
                    {!emailTo ? (
                      <div className="mb-2">
                        <h5 className="text-danger">
                          This work order has no contact email
                        </h5>
                        {/* Contact Info */}
                        <div className="mb-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <label className="form-label mb-1">
                              Email To ( "Separate multiple by comma" ){" "}
                              {userSmtp?.smtpEmail && (
                                <small className="text-muted">
                                  (Email from : {userSmtp.smtpEmail})
                                </small>
                              )}
                            </label>
                            <button
                              onClick={() => setShowCustomerContact(true)}
                              className="btn btn-info btn-sm mb-1"
                            >
                              Select Contact
                            </button>
                          </div>
                          <input
                            type="email"
                            className="form-control"
                            value={emailTo}
                            onChange={(e) => setEmailTo(e.target.value)}
                          />
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Contact Info — the hint and the buttons sit on
                            their own row above so the field itself lines up
                            in the same label/input grid as Subject below. */}
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <small className="text-muted">
                            Separate multiple by comma
                            {userSmtp?.smtpEmail && (
                              <> &nbsp;·&nbsp; Email from : {userSmtp.smtpEmail}</>
                            )}
                          </small>
                          <div className="d-flex align-items-center gap-1">
                            {templateSettings?.ccSalesPerson && (
                              <button
                                onClick={(e) => {
                                  addSalesPersonEmailToList();
                                }}
                                className="btn btn-primary btn-sm"
                              >
                                Insert Sales Person
                              </button>
                            )}
                            <button
                              onClick={() => setShowCustomerContact(true)}
                              className="btn btn-info btn-sm"
                            >
                              Select Contact
                            </button>
                          </div>
                        </div>
                        {/* Label column is a fixed width rather than col-2 so
                            the gap tracks the text, not a twelfth of the modal;
                            g-2 trims the row gutter too. Email To and Subject
                            share the same width so their inputs stay aligned. */}
                        <div className="row align-items-center mb-2 g-2">
                          <label
                            htmlFor="emailTo"
                            className="col-auto form-label mb-0"
                            style={{ width: LABEL_COL_WIDTH }}
                          >
                            Email To
                          </label>
                          <div className="col">
                            <input
                              id="emailTo"
                              type="email"
                              className="form-control"
                              value={emailTo}
                              onChange={(e) => setEmailTo(e.target.value)}
                            />
                          </div>
                        </div>

                        {isLoading && <Spinner />}

                        {/* ******************** Email To && Email Body ************************* */}
                        {/* The sender is shown inline in the "Email To" label
                            above rather than as its own read-only row, which
                            frees a line of modal height. Same treatment as the
                            quote-send modal. */}
                        {!userSmtp?.smtpEmail && (
                          <div className="mb-2 text-danger small">
                            Please Add SMTP Settings in user settings.
                          </div>
                        )}
                        {/* Subject */}
                        <div className="row align-items-center mb-2 g-2">
                          <label
                            htmlFor="msgBody"
                            className="col-auto form-label mb-0"
                            style={{ width: LABEL_COL_WIDTH }}
                          >
                            Subject
                          </label>
                          <div className="col">
                            <input
                              type="text"
                              value={subject || ""}
                              required
                              onChange={(e) => {
                                setSubject((prev) => e.target.value);
                              }}
                              className="form-control"
                              placeholder="Enter Subject"
                            />
                          </div>
                        </div>

                        {/* ******************** Attachments ************************* */}
                        <div className="d-flex justify-content-between align-items-center mt-2">
                          <h5 className="my-0">Attachments Sent in Email</h5>
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => {
                              setShowFilesModal(true);
                            }}
                          >
                            Choose
                          </button>
                        </div>
                        <hr className="mb-1 mt-1" />
                        <div className="d-flex gap-2 flex-wrap">
                          {selectedFiles &&
                            selectedFiles?.map((item) => (
                              <div
                                key={item}
                                className="bg-white custom-shadow-sm px-2 py-1 d-flex gap-2 rounded-3"
                              >
                                <div>{getFileName(item)}</div>
                                <div
                                  role="button"
                                  onClick={() => {
                                    handleDeleteFile(item);
                                  }}
                                  className="bg-danger text-white px-1 rounded-3"
                                >
                                  X
                                </div>
                              </div>
                            ))}
                        </div>

                        {/* Pdf Options */}
                        <div className="d-flex align-items-center gap-3 my-1">
                          <div className="d-flex align-items-center gap-2">
                            <input
                              type="checkbox"
                              id="showStatuses"
                              checked={templateSettings?.showStatuses || false}
                              onChange={(e) =>
                                setTemplateSettings((prev) => ({
                                  ...prev,
                                  showStatuses: e.target.checked,
                                }))
                              }
                              data-switch="success"
                            />
                            <label
                              htmlFor="showStatuses"
                              data-on-label="Yes"
                              data-off-label="No"
                            ></label>
                            <span className="fw-bold ms-1">
                              Show Statuses in PDF
                            </span>
                          </div>
                        </div>

                        {/* Email Body */}
                        <div
                          className="mt-1 flex-grow-1 d-flex flex-column"
                          style={{ minHeight: 0 }}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <label htmlFor="msgBody" className="form-label mb-1">
                              Email Body
                            </label>
                            {/* Hidden per request -- Email Template feature hidden in Settings, to be deleted later */}
                            <div className="d-none">
                              <button
                                type="button"
                                onClick={() => {
                                  setShowTemplateModal(true);
                                }}
                                className="btn btn-sm btn-success"
                              >
                                Select Template
                              </button>
                            </div>
                          </div>

                          <ReactQuill
                            modules={modules}
                            theme="snow"
                            className="send-approval-quill"
                            value={emailBody}
                            onChange={(val) => setEmailBody(val)}
                          />
                        </div>
                      </>
                    )}
                    </div>

                    {/*   Buttons
                     ********************************************* */}
                    <div className="text-end mt-2 mb-1">
                      <button
                        type="button"
                        className="btn btn-secondary me-1"
                        onClick={() => {
                          handleClose();
                        }}
                      >
                        Close
                      </button>
                      {templateSettings?.sendPdf ? (
                        // Only render BlobProvider after all images are
                        // pre-downloaded so the PDF always contains them
                        loadingPdfAssets ? (
                          <>
                            <button
                              type="button"
                              disabled
                              className="btn btn-info me-1"
                            >
                              <span
                                className="spinner-border spinner-border-sm me-1"
                                role="status"
                              />
                              Preparing PDF...
                            </button>
                            <button
                              type="button"
                              disabled
                              className="btn btn-primary"
                            >
                              <span
                                className="spinner-border spinner-border-sm me-1"
                                role="status"
                              />
                              Preparing PDF...
                            </button>
                          </>
                        ) : (
                          <BlobProvider
                            document={
                              <WOPdf
                                productsList={
                                  woProductsWithSignedImages?.length
                                    ? woProductsWithSignedImages
                                    : woProductsList ||
                                      selectedWorkOrder?.WOProducts ||
                                      []
                                }
                                hideHeader={true}
                                approvalColorMap={approvalColorMap}
                                selectedWO={{
                                  ...selectedWorkOrder,
                                  allApprovedAt: approvedAt,
                                }}
                                showInvoicePage={false}
                                locationLogo={locationLogo}
                                showStatuses={templateSettings?.showStatuses}
                              />
                            }
                          >
                            {({ blob }) => (
                              <>
                                <button
                                  type="button"
                                  disabled={isLoading || !blob}
                                  className="btn btn-info me-1"
                                  onClick={() => {
                                    sendApproval(false, blob);
                                  }}
                                >
                                  View Approval
                                </button>
                                <button
                                  type="button"
                                  disabled={isLoading || !blob}
                                  className="btn btn-primary"
                                  onClick={() => {
                                    sendApproval(true, blob);
                                  }}
                                >
                                  Send Approval & Pdf
                                </button>
                              </>
                            )}
                          </BlobProvider>
                        )
                      ) : (
                        <>
                          <button
                            type="button"
                            disabled={isLoading}
                            className="btn btn-info me-1"
                            onClick={() => {
                              sendApproval(false);
                            }}
                          >
                            View Approval
                          </button>
                          <button
                            type="button"
                            disabled={isLoading}
                            className="btn btn-primary"
                            onClick={() => {
                              sendApproval();
                            }}
                          >
                            Send Approval
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>
      {/*    Select Email template
       ********************************************* */}
      <Modal
        size="lg"
        contentClassName="bg-transparent"
        show={showTemplateModal}
        onHide={() => setShowTemplateModal(false)}
        style={{
          zIndex: 1000000002,
        }}
      >
        <Modal.Body className="bg-white custom-shadow custom-border-radius">
          <h4 className="h3 mb-3">Select Files</h4>

          <SelectEmailTemplate
            showModal={setShowTemplateModal}
            applyTemplate={applyTemplate}
          />
        </Modal.Body>
      </Modal>
      {/*    Select Files
       ********************************************* */}
      <Modal
        size="lg"
        contentClassName="bg-transparent"
        show={showFilesModal}
        onHide={() => setShowFilesModal(false)}
        style={{
          zIndex: 1000000002,
        }}
      >
        <Modal.Body className="bg-white custom-shadow custom-border-radius">
          <h4 className="h3 mb-3">Select Files</h4>
          <WOFileManager
            selectedWO={selectedWorkOrder}
            setSelectedFiles={setSelectedFiles}
            setShowFilesModal={setShowFilesModal}
          />
        </Modal.Body>
      </Modal>
      {/*    Select Customer Contact
       ********************************************* */}
      <Modal
        size="lg"
        contentClassName="bg-transparent"
        show={showCustomerContact}
        onHide={() => setShowCustomerContact(false)}
        style={{
          zIndex: 1000000002,
        }}
      >
        <Modal.Body>
          <CustomerContactSelect
            handleSelectContact={handleSelectContact}
            customerId={selectedWorkOrder?.customer?.customerId}
            isMultiSelect={true}
            isPrimaryId={false}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
}
